// Writing rows: 빠른 입력 (amount → category → save, the rest from
// defaults; design 02 3.3) and the sheet that changes or deletes a row. On
// a phone the amount's 다음 puts the keyboard away while no category is
// picked (the categories show), then its 완료 saves.
import { type FormEvent, type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react'

import { enterSends, hasFinePointer, isComposing } from '../../lib/ime'
import { ErrorText, Field, Sheet } from '../../ui/common'
import type { Id, RowKind, TensorItem } from '../api'
import { engine, lastPayment } from '../engine'
import { amountInput, dayLabel, parseAmount, today, validDay, won } from '../format'
import type { RowFields, RowPatch, ShownRow } from '../model'
import { useT } from '../store'
import { MEMO_MAX } from '../csv'
import { itemName, itemsOf } from '../views'
import { AmountField, type Current, ItemSelect, KindSwitch } from './common'

/** The top-level category income goes under ("수입"), if there is one. */
function incomeTop(items: Record<number, TensorItem>): TensorItem | undefined {
  return itemsOf(items, 'category').find((i) => i.name === '수입')
}

/** My person item: the one with my name. */
function myPerson(items: Record<number, TensorItem>, myName: string): Id | null {
  return itemsOf(items, 'person').find((i) => i.name === myName)?.id ?? null
}

function memoKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
  // PC: Enter saves, Shift+Enter is a new line; never while composing
  // Korean (design 8.7). Phone: the save button only.
  if (enterSends(e, hasFinePointer())) {
    e.preventDefault()
    e.currentTarget.form?.requestSubmit()
  }
}

function chars(s: string): number {
  return Array.from(s).length
}

export function QuickEntry({ cur, open, onClose }: { cur: Current; open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="빠른 입력">
      {open && <QuickForm cur={cur} onDone={onClose} />}
    </Sheet>
  )
}

function QuickForm({ cur, onDone }: { cur: Current; onDone: () => void }) {
  const items = cur.book?.items ?? {}
  const myName = useT((s) => s.me?.name ?? '')
  const [kind, setKind] = useState<RowKind>('expense')
  const [amount, setAmount] = useState('')
  const [top, setTop] = useState<Id | null>(null)
  const [child, setChild] = useState<Id | null>(null)
  const [more, setMore] = useState(false)
  const [day, setDay] = useState(() => today())
  const [person, setPerson] = useState<Id | null>(() => myPerson(items, myName))
  const [payment, setPayment] = useState<Id | null>(() => {
    const p = lastPayment(cur.workbook.id)
    return p && items[p] && !items[p].deleted ? p : null
  })
  const [memo, setMemo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const amountRef = useRef<HTMLInputElement>(null)

  // the amount first, with the phone's number keys (after the sheet took
  // the focus for itself)
  useEffect(() => {
    const t = setTimeout(() => amountRef.current?.focus(), 0)
    return () => clearTimeout(t)
  }, [])

  const income = incomeTop(items)
  // a category is there (picked, or 수입 for income): Enter saves
  const picked = top !== null || (kind === 'income' && income !== undefined)
  const tops = useMemo(() => {
    const all = itemsOf(items, 'category')
    if (kind === 'income') return income ? itemsOf(items, 'category', income.id) : all
    return all.filter((i) => i.id !== income?.id)
  }, [items, kind, income])
  const children = top && !(kind === 'income' && income) ? itemsOf(items, 'category', top) : []

  function chooseKind(k: RowKind) {
    setKind(k)
    setTop(null)
    setChild(null)
  }

  function category(): Id | null {
    if (child) return child
    if (top) return top
    return kind === 'income' && income ? income.id : null
  }

  function submit(e: FormEvent) {
    e.preventDefault()
    const n = parseAmount(amount)
    if (n === null) {
      setError('금액을 넣어 주세요.')
      return
    }
    if (!validDay(day)) {
      setError('날짜를 확인해 주세요.')
      return
    }
    if (chars(memo) > MEMO_MAX) {
      setError(`메모는 ${MEMO_MAX}자까지입니다.`)
      return
    }
    const fields: RowFields = {
      kind,
      day,
      amount: n,
      person_item: person,
      category_item: category(),
      payment_item: payment,
      memo: memo.trim().normalize('NFC'),
    }
    engine.addRow(cur.workbook.id, fields)
    onDone()
  }

  return (
    <form className="t-form" onSubmit={submit}>
      <KindSwitch kind={kind} onChange={chooseKind} />
      <AmountField
        value={amount}
        onChange={setAmount}
        inputRef={amountRef}
        enterKeyHint={picked ? 'done' : 'next'}
        onKeyDown={(e) => {
          // a phone's 다음 before a category: the keyboard goes and the categories show
          if (e.key !== 'Enter' || isComposing(e) || picked || hasFinePointer()) return
          e.preventDefault()
          e.currentTarget.blur()
        }}
      />
      <div className="field">
        <span className="field-label" id="t-cat-label">
          분류
        </span>
        <div className="t-chips" role="group" aria-labelledby="t-cat-label">
          {tops.map((i) => (
            <button
              key={i.id}
              type="button"
              className={`chip${top === i.id ? ' on' : ''}`}
              aria-pressed={top === i.id}
              onClick={() => {
                setTop(top === i.id ? null : i.id)
                setChild(null)
              }}
            >
              {i.name}
            </button>
          ))}
        </div>
        {children.length > 0 && (
          <div className="t-chips t-sub" role="group" aria-label={`${itemName(items, top)} 세부 분류`}>
            {children.map((i) => (
              <button
                key={i.id}
                type="button"
                className={`chip${child === i.id ? ' on' : ''}`}
                aria-pressed={child === i.id}
                onClick={() => setChild(child === i.id ? null : i.id)}
              >
                {i.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {more ? (
        <>
          <Field label="날짜">
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} required />
          </Field>
          <Field label="사람">
            <ItemSelect items={items} role="person" value={person} onChange={setPerson} />
          </Field>
          <Field label="결제수단">
            <ItemSelect items={items} role="payment" value={payment} onChange={setPayment} />
          </Field>
          <Field label="메모">
            <textarea rows={2} value={memo} onChange={(e) => setMemo(e.target.value)} onKeyDown={memoKeyDown} />
          </Field>
        </>
      ) : (
        <button type="button" className="t-defaults" onClick={() => setMore(true)}>
          <span>
            {day === today() ? '오늘' : dayLabel(day)} · {itemName(items, person)} · {itemName(items, payment)}
          </span>
          <span className="link">날짜·사람·결제수단·메모</span>
        </button>
      )}
      <ErrorText text={error} />
      <button className="primary" type="submit" disabled={parseAmount(amount) === null}>
        저장
      </button>
    </form>
  )
}

/** Changing a row: every field; and deleting it. */
export function RowSheet({ cur, row, onClose }: { cur: Current; row: ShownRow | null; onClose: () => void }) {
  return (
    <Sheet open={row !== null} onClose={onClose} title="거래 고치기">
      {row && <RowForm key={row.client_id} cur={cur} row={row} onDone={onClose} />}
    </Sheet>
  )
}

function RowForm({ cur, row: now, onDone }: { cur: Current; row: ShownRow; onDone: () => void }) {
  const items = cur.book?.items ?? {}
  // the row as it was when the sheet opened: only what is changed here is
  // sent, with the version seen then (design 02 4.4: a change made
  // elsewhere meanwhile is kept, or the server says stale)
  const [row] = useState(now)
  const [kind, setKind] = useState<RowKind>(row.kind)
  const [day, setDay] = useState(row.day)
  const [amount, setAmount] = useState(() => amountInput(String(row.amount)))
  const [category, setCategory] = useState(row.category_item)
  const [person, setPerson] = useState(row.person_item)
  const [payment, setPayment] = useState(row.payment_item)
  const [memo, setMemo] = useState(row.memo)
  const [error, setError] = useState<string | null>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    const n = parseAmount(amount)
    if (n === null) {
      setError('금액을 넣어 주세요.')
      return
    }
    if (!validDay(day)) {
      setError('날짜를 확인해 주세요.')
      return
    }
    if (chars(memo) > MEMO_MAX) {
      setError(`메모는 ${MEMO_MAX}자까지입니다.`)
      return
    }
    const m = memo.trim().normalize('NFC')
    const patch: RowPatch = {}
    if (kind !== row.kind) patch.kind = kind
    if (day !== row.day) patch.day = day
    if (n !== row.amount) patch.amount = n
    if (category !== row.category_item) patch.category_item = category
    if (person !== row.person_item) patch.person_item = person
    if (payment !== row.payment_item) patch.payment_item = payment
    if (m !== row.memo) patch.memo = m
    if (Object.keys(patch).length > 0) engine.editRow(cur.workbook.id, row, patch)
    onDone()
  }

  function remove() {
    if (!confirm(`${dayLabel(now.day)} ${won(now.amount)} 거래를 지울까요?`)) return
    engine.deleteRow(cur.workbook.id, now)
    onDone()
  }

  return (
    <form className="t-form" onSubmit={submit}>
      <KindSwitch kind={kind} onChange={setKind} />
      <Field label="날짜">
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)} required />
      </Field>
      <AmountField value={amount} onChange={setAmount} />
      <Field label="분류">
        <ItemSelect items={items} role="category" value={category} onChange={setCategory} />
      </Field>
      <Field label="사람">
        <ItemSelect items={items} role="person" value={person} onChange={setPerson} />
      </Field>
      <Field label="결제수단">
        <ItemSelect items={items} role="payment" value={payment} onChange={setPayment} />
      </Field>
      <Field label="메모">
        <textarea rows={2} value={memo} onChange={(e) => setMemo(e.target.value)} onKeyDown={memoKeyDown} />
      </Field>
      <ErrorText text={error} />
      <div className="t-buttons">
        <button type="button" className="danger-btn" onClick={remove}>
          지우기
        </button>
        <button className="primary grow" type="submit">
          저장
        </button>
      </div>
    </form>
  )
}
